import { sortBy, sum, take } from 'lib/array/arrayUtils'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import TourV2DepartureTile from './TourV2DepartureTile'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import generateTourV2CheckoutItem from 'lib/tours/generateTourV2CheckoutItems'
import { addToCheckoutCart, initialiseCheckout } from 'actions/CheckoutActions'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import { addToCartGTM, addTourToCart } from 'analytics/eventDefinitions'
import { addToCart } from 'analytics/snowplow/events'
import { fireEcommerceGTMEvent, addGTMEvent } from 'api/googleTagManager'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import * as Analytics from 'analytics/analytics'
import GeoContext from 'contexts/geoContext'
import { areDeparturesSoldOut, getDepartureCheapestOptionForTravellers } from 'lib/tours/tourDepartureUtils'
import TourV2DeparturesSoldOutTile from './TourV2DeparturesSoldOutTile'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { hasSelectedRooms } from 'lib/offer/offerPageUtil'
import { ALL_TOUR_PACKAGE_TYPE } from 'constants/tours'

const ListContainer = styled.div`
  border-top: 1px solid ${props => props.theme.palette.neutral.default.six};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};

  > * + * {
    border-top: 1px solid ${props => props.theme.palette.neutral.default.six};
  }
`

const ShowAllButton = styled(TextButton)`
  align-self: center;
`

const pageSize = 6

interface Props {
  offer: App.Tours.TourV2Offer
  departures: Array<App.Tours.TourV2OfferDeparture>
  variation: App.Tours.TourV2OfferVariation
  travellers: Array<OfferPageRoom>
  packageType?: string
  ref?: React.Ref<HTMLDivElement>
}

function TourV2DepartureList(props: Props) {
  const { offer, travellers, departures, variation, packageType, ref } = props
  const intl = useIntl()
  const [page, setPage] = useState<number>(1)
  const { currentCurrency } = useContext(GeoContext)

  useEffect(() => {
    // Showing a different list of departures
    // Set our page back to the first page
    setPage(1)
  }, [departures])

  const dispatch = useAppDispatch()
  const offerPageDispatch = useOfferPageDispatch()
  const sortedDepartures = useMemo(() => {
    return sortBy(departures, departure => new Date(departure.startDate), 'asc')
  }, [departures])

  const limit = page * pageSize

  const onSelect = useCallback((departure: App.Tours.TourV2OfferDeparture, keepBrowsing: boolean = false) => {
    // have to select travellers first
    if (!hasSelectedRooms(travellers)) {
      offerPageDispatch({ type: OfferPageActions.setKeepBrowsing, keepBrowsing })
      offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
      offerPageDispatch({ type: OfferPageActions.setTourV2Departure, departure })
      return
    }

    const items = travellers.map(traveller => {
      const resolvedPackageType = packageType && packageType !== ALL_TOUR_PACKAGE_TYPE ? packageType : undefined
      const option = getDepartureCheapestOptionForTravellers(departure, traveller, resolvedPackageType)
        ?? getDepartureCheapestOptionForTravellers(departure, traveller)
      if (option) {
        return generateTourV2CheckoutItem(
          offer,
          departure,
          traveller,
          option,
        )
      }
    }).filter(item => !!item)

    if (keepBrowsing) {
      dispatch(addToCheckoutCart(items))
      showSnackbar(intl.formatMessage({
        id: 'ODh1o1FdZz',
        defaultMessage: 'Your item has been added to the cart.',
        description: 'Snackbar message when item is added to cart',
      }), 'success', {
        action: {
          label: intl.formatMessage({
            id: '6l8uzXWVLE',
            defaultMessage: 'View cart',
            description: 'Snackbar action label to view cart',
          }),
          to: '/cart',
        },
      })
    } else {
      dispatch(initialiseCheckout(items))
      dispatch(navigateToCheckout())
    }

    const totalTravellers = sumUpOccupancies(travellers)
    const priceTotal = sum(items, item => item.total ?? 0)
    fireEcommerceGTMEvent(addToCartGTM(offer, currentCurrency, priceTotal, totalTravellers, 'tour'))
    addGTMEvent(addTourToCart(offer, currentCurrency, priceTotal, totalTravellers))
    Analytics.trackEvent(addToCart())
  }, [travellers, dispatch, offer, currentCurrency, offerPageDispatch, packageType, intl])

  const soldOutReason = useMemo(() => {
    return areDeparturesSoldOut(departures, travellers)
  }, [departures, travellers])

  const showTravellersFilter = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [offerPageDispatch])

  const onShowMore = useCallback(() => setPage(page => page + 1), [])

  return <Group direction="vertical" gap={32} ref={ref}>
    <ListContainer data-testid="tour-package-list">
      {soldOutReason && <TourV2DeparturesSoldOutTile
        reason={soldOutReason}
        onUpdateTravelers={showTravellersFilter}
      />}
      {!soldOutReason && take(sortedDepartures, limit).map((departure) => {
        return <TourV2DepartureTile
          key={departure.id}
          departure={departure}
          travellers={travellers}
          offer={offer}
          onSelect={onSelect}
          variation={variation}
          packageType={packageType}
        />
      })}
    </ListContainer>
    {!soldOutReason && departures.length > limit && <ShowAllButton
      kind="secondary"
      onClick={onShowMore}
    >
      {intl.formatMessage({
        id: 'qyacJ9vPIg',
        defaultMessage: 'Show more departures',
        description: 'Button text to show more departures',
      })}
    </ShowAllButton>}
  </Group>
}

type MappedProps = Pick<Props, 'travellers' | 'variation'>

export default withOfferPageState((state): MappedProps => ({
  travellers: state.rooms,
  variation: state.tourV2Variation!,
}), TourV2DepartureList)
